import React, { Component } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";

import { organizationReportService } from "_services/organizationReport.service";
import {
  DEMO_FACILITY_GROUP,
  STATIC_DEMO,
  resolveSection,
  isEmptyKpis,
  isEmptyFunnel,
  isEmptyArray,
  isEmptyActivity,
  isEmptySpeed,
} from "./staticDemo";
import "./organizationReport.css";

const TAILWIND_CDN_ID = "tailwind-cdn-orgreport";

function injectTailwindCdn() {
  if (document.getElementById(TAILWIND_CDN_ID)) return;
  // Disable Tailwind preflight so it doesn't reset MUI's global styles on other pages.
  const cfg = document.createElement("script");
  cfg.id = `${TAILWIND_CDN_ID}-cfg`;
  cfg.text = "window.tailwind = window.tailwind || {}; window.tailwind.config = { corePlugins: { preflight: false } };";
  document.head.appendChild(cfg);

  const s = document.createElement("script");
  s.id = TAILWIND_CDN_ID;
  s.src = "https://cdn.tailwindcss.com";
  document.head.appendChild(s);

  if (!document.getElementById("inter-font-orgreport")) {
    const link = document.createElement("link");
    link.id = "inter-font-orgreport";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap";
    document.head.appendChild(link);
  }
}

export default class OrganizationReport extends Component {
  constructor(props) {
    super(props);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.state = {
      organizationId: DEMO_FACILITY_GROUP.id,
      startDate: thirtyDaysAgo,
      endDate: today,
      loading: false,
      data: null,
      error: null,
      hasGenerated: false,
    };
  }

  componentDidMount() {
    injectTailwindCdn();
  }

  handleGenerate = async () => {
    const { startDate, endDate } = this.state;
    if (!startDate || !endDate) return;
    this.setState({ loading: true, error: null, hasGenerated: true });
    try {
      const data = await organizationReportService.fetchReport({
        facilityIds: DEMO_FACILITY_GROUP.facilityIds,
        startDate,
        endDate,
      });
      this.setState({ data, loading: false });
    } catch (e) {
      this.setState({ error: e.message, loading: false, data: null });
    }
  };

  render() {
    const { startDate, endDate, organizationId, loading, data, hasGenerated } = this.state;

    const kpis = resolveSection(data && data.networkKpis, STATIC_DEMO.networkKpis, isEmptyKpis);
    const funnel = resolveSection(data && data.funnel, STATIC_DEMO.funnel, isEmptyFunnel);
    const activity = resolveSection(
      data && data.activityByDay,
      STATIC_DEMO.activityByDay,
      isEmptyActivity
    );
    const backlog = resolveSection(
      data && data.canAcceptBacklog,
      STATIC_DEMO.canAcceptBacklog,
      isEmptyArray
    );
    const facilities = resolveSection(
      data && data.facilities,
      STATIC_DEMO.facilities,
      isEmptyArray
    );
    const speed = resolveSection(
      data && data.speedDistribution,
      STATIC_DEMO.speedDistribution,
      isEmptySpeed
    );
    const alerts = resolveSection(
      data && data.criticalAlerts,
      STATIC_DEMO.criticalAlerts,
      isEmptyArray
    );

    return (
      <div className="orgreport-root">
        <GridContainer>
          <GridItem xs={12}>
            <Card>
              <CardBody>
                <Box display="flex" flexWrap="wrap" gridGap={16} alignItems="center" mb={2}>
                  <FormControl style={{ minWidth: 240 }}>
                    <InputLabel id="org-dropdown-label">Organization Name</InputLabel>
                    <Select
                      labelId="org-dropdown-label"
                      value={organizationId}
                      onChange={(e) => this.setState({ organizationId: e.target.value })}
                    >
                      <MenuItem value={DEMO_FACILITY_GROUP.id}>
                        {DEMO_FACILITY_GROUP.name}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      label="Start Date"
                      format="MM/dd/yyyy"
                      value={startDate}
                      onChange={(d) => this.setState({ startDate: d })}
                      maxDate={endDate || new Date()}
                    />
                    <KeyboardDatePicker
                      label="End Date"
                      format="MM/dd/yyyy"
                      value={endDate}
                      onChange={(d) => this.setState({ endDate: d })}
                      minDate={startDate}
                      maxDate={new Date()}
                    />
                  </MuiPickersUtilsProvider>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.handleGenerate}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : "Generate Report"}
                  </Button>
                </Box>

                {hasGenerated ? (
                  /* All Tailwind-styled markup is scoped under .orgreport-body to limit reset/utility impact */
                  <div className="orgreport-body">
                    <ReportBody
                      organization={DEMO_FACILITY_GROUP}
                      window={{ startDate, endDate }}
                      kpis={kpis}
                      funnel={funnel}
                      activity={activity}
                      backlog={backlog}
                      facilities={facilities}
                      speed={speed}
                      alerts={alerts}
                    />
                  </div>
                ) : (
                  <Box
                    py={6}
                    textAlign="center"
                    style={{ color: "#6b6969", fontSize: "0.9rem" }}
                  >
                    Select an Organization and date range, then click{" "}
                    <strong>Generate Report</strong> to view the dashboard.
                  </Box>
                )}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

/* ------------------------------------------------------------------ *
 *  REPORT BODY — port of newgen-executive-dashboard-v4-2026-05-14.html
 *  Inline styles + Tailwind utility classes match the original mock.
 * ------------------------------------------------------------------ */

function ReportBody({
  organization,
  window: dateWindow,
  kpis,
  funnel,
  activity,
  backlog,
  facilities,
  speed,
  alerts,
}) {
  const windowLabel = `${formatDate(dateWindow.startDate)} – ${formatDate(dateWindow.endDate)}`;
  return (
    <div style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <Hero kpis={kpis} windowLabel={windowLabel} organization={organization} />
      <div style={{ marginTop: 16 }}>
        <CriticalAlerts alerts={alerts} />
      </div>
      <div style={{ marginTop: 16 }}>
        <ImpactBanner kpis={kpis} windowLabel={windowLabel} />
      </div>
      <div style={{ marginTop: 16 }}>
        <NetworkKpis kpis={kpis} />
      </div>
      <div style={{ marginTop: 16 }}>
        <FunnelSection funnel={funnel} kpis={kpis} />
      </div>
      <div style={{ marginTop: 16 }}>
        <ActivityByDay activity={activity} />
      </div>
      <div style={{ marginTop: 16 }}>
        <CanAcceptBacklog backlog={backlog} />
      </div>
      <div style={{ marginTop: 16 }}>
        <FacilityNetwork facilities={facilities} />
      </div>
      <div style={{ marginTop: 16 }}>
        <SpeedToFinalize speed={speed} kpis={kpis} />
      </div>
      <div style={{ marginTop: 16 }}>
        <MaxAllySection />
      </div>
      <div style={{ textAlign: "center", padding: "16px 0 8px", fontSize: "0.72rem", color: "#b0aeae" }}>
        MAXMRJ Executive Dashboard · {organization.name} · {windowLabel} · Demo Preview<br />
        MaxAlly section: ⚠ MOCK/DEMO DATA · Future Organization hierarchy feature
      </div>
    </div>
  );
}

function Hero({ kpis, windowLabel, organization }) {
  return (
    <div
      style={{
        background: "linear-gradient(135deg,#252424 0%,#1e1d1d 100%)",
        borderRadius: 16,
        padding: "28px 36px",
        borderBottom: "3px solid #00a7a6",
        color: "#fff",
      }}
    >
      <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#00a7a6", marginBottom: 10 }}>
        {organization.name} · Executive Summary · {windowLabel}
      </div>
      <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#fff", lineHeight: 1.55 }}>
        At full network capacity, MAXMRJ is projected to deliver{" "}
        <span style={{ color: "#00a7a6" }}>$350,000+</span> in combined annual value —
        coordinator efficiency savings plus{" "}
        <span style={{ color: "#00a7a6" }}>$17K–$34K</span> in VBP penalty avoidance per facility
        improving 30-day readmission performance.
      </div>
      <div style={{ marginTop: 12, fontSize: "0.78rem", color: "#9b9898", lineHeight: 1.5 }}>
        Today's baseline:{" "}
        <strong style={{ color: "#f0f0f0" }}>
          {kpis.activeFacilities} active facilities · {kpis.totalSends} sends ·{" "}
          {kpis.finalized} finalized referrals · {kpis.placementRatePct}% placement rate.
        </strong>
      </div>
    </div>
  );
}

function CriticalAlerts({ alerts }) {
  if (!alerts || alerts.length === 0) {
    return (
      <div style={{ background: "#f0fff4", border: "1px solid #86efac", borderRadius: 14, padding: "16px 24px", color: "#166534", fontWeight: 600 }}>
        ✓ No coordinator-blocked Can Accept cases in this window.
      </div>
    );
  }
  return (
    <div style={{ background: "#fff0f0", border: "2px solid #fca5a5", borderRadius: 14, padding: "20px 28px" }}>
      <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#dc2626", marginBottom: 12 }}>
        ⚠ Critical — Coordinator Action Required · {alerts.length}{" "}
        {alerts.length === 1 ? "Facility" : "Facilities"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: alerts.length > 1 ? "1fr 1fr" : "1fr", gap: 20 }}>
        {alerts.map((a, i) => (
          <div key={i} style={{ background: "#fff", border: "1px solid #fca5a5", borderRadius: 10, padding: 16 }}>
            <div style={{ fontWeight: 800, color: "#dc2626", fontSize: "1rem", marginBottom: 6 }}>
              {a.facilityName}
            </div>
            <div style={{ fontSize: "0.82rem", color: "#252424", lineHeight: 1.7 }}>
              <strong style={{ color: "#dc2626" }}>
                {a.openCanAccepts} open Can Accept{a.openCanAccepts === 1 ? "" : "s"} · oldest {a.oldestWaitDays}d:
              </strong>{" "}
              {a.providers.join(" · ")}
              <div style={{ color: "#6b6969", fontSize: "0.75rem", marginTop: 4 }}>
                → coordinator finalize step pending
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImpactBanner({ kpis, windowLabel }) {
  return (
    <div style={{ background: "linear-gradient(135deg,#f0fffe 0%,#eafffe 100%)", borderRadius: 12, padding: 20, border: "1px solid #99eeed" }}>
      <div className="section-label" style={{ marginBottom: 12 }}>
        MAXMRJ Impact · {windowLabel}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        <KpiCard
          big="$350K+"
          label="projected annual value · full network"
          sub="coordinator savings + VBP reduction · 20 facilities"
          footnote="$16.2K projected today · current active facilities"
          topBorder
        />
        <KpiCard
          big="~1,000"
          label="est. placements/year · full network"
          sub="all 20 facilities · annualized"
          footnote={`${kpis.finalized} finalized this period · ${kpis.activeFacilities} facilities`}
        />
        <KpiCard
          big="~800"
          label="est. coordinator hrs saved/yr"
          sub="all 20 facilities · ~$28K at $35/hr equiv."
          footnote={`based on ~40 min saved per referral`}
        />
        <KpiCard
          big={
            <span>
              {kpis.activeFacilities}
              <span style={{ fontSize: "1rem", fontWeight: 700, color: "#6b6969" }}>
                {" "}/ {kpis.totalFacilities}
              </span>
            </span>
          }
          label="facilities active"
          sub={`${Math.max(kpis.totalFacilities - kpis.activeFacilities, 0)} dark / onboarding`}
        />
      </div>
    </div>
  );
}

function KpiCard({ big, label, sub, footnote, topBorder }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e1e0e0",
        borderRadius: 14,
        padding: 16,
        textAlign: "center",
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
        ...(topBorder ? { borderTop: "3px solid #00a7a6" } : {}),
      }}
    >
      <div style={{ fontSize: "2.1rem", fontWeight: 800, color: "#00a7a6", lineHeight: 1 }}>{big}</div>
      <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#252424", marginTop: 2 }}>{label}</div>
      <div style={{ fontSize: "0.72rem", color: "#6b6969", marginTop: 4 }}>{sub}</div>
      {footnote && (
        <div style={{ fontSize: "0.67rem", color: "#b0aeae", marginTop: 3, fontStyle: "italic" }}>
          {footnote}
        </div>
      )}
    </div>
  );
}

function NetworkKpis({ kpis }) {
  return (
    <div>
      <SectionAccent title="Network Health" sub={`${kpis.activeFacilities} active facilities`} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 16 }}>
        <SmallKpi label="Referrals Sent" value={kpis.totalSends} sub={`${kpis.uniqueReferrals} unique referral IDs`} />
        <SmallKpi label="Finalized" value={kpis.finalized} sub={`of ${kpis.uniqueReferrals} unique IDs`} accent="#16a34a" extra={`${kpis.placementRatePct}% placement rate`} />
        <SmallKpi label="Can Accept — Waiting" value={kpis.canAcceptOpen} sub="all within 28d" accent="#dc2626" extra="→ coordinator action needed" leftBorder="#dc2626" />
        <SmallKpi
          label="Median to Finalize"
          value={kpis.medianMinutesToFinalize != null ? `${kpis.medianMinutesToFinalize} min` : "—"}
          sub={kpis.avgHoursToFinalize != null ? `${kpis.avgHoursToFinalize} hr average` : ""}
        />
        <SmallKpi label="Finalized <24h" value={`${kpis.pctWithin24h}%`} sub={`${kpis.pctWithin48h}% within 48h`} />
        <SmallKpi label="Active Facilities" value={kpis.activeFacilities} sub={`of ${kpis.totalFacilities} total`} />
      </div>
    </div>
  );
}

function SmallKpi({ label, value, sub, accent, extra, leftBorder }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e1e0e0",
        borderRadius: 14,
        padding: 16,
        ...(leftBorder ? { borderLeft: `3px solid ${leftBorder}` } : {}),
      }}
    >
      <div className="section-label">{label}</div>
      <div style={{ fontSize: "2.1rem", fontWeight: 800, color: accent || "#00a7a6", lineHeight: 1, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: "0.78rem", color: "#6b6969", marginTop: 4 }}>{sub}</div>
      {extra && (
        <div style={{ fontSize: "0.72rem", color: accent || "#16a34a", fontWeight: 600, marginTop: 4 }}>{extra}</div>
      )}
    </div>
  );
}

function FunnelSection({ funnel, kpis }) {
  const stages = [
    { label: "TOTAL SENDS", value: funnel.sent, color: "#00a7a6", sub: `${kpis.uniqueReferrals} unique IDs` },
    { label: "ENGAGED", value: funnel.engaged, color: "#00b8b7", sub: pctOf(funnel.engaged, kpis.uniqueReferrals) },
    { label: "RESPONDED", value: funnel.responded, color: "#00c9c8", sub: pctOf(funnel.responded, kpis.uniqueReferrals) },
    { label: "FINALIZED", value: funnel.finalized, color: "#16a34a", sub: pctOf(funnel.finalized, kpis.uniqueReferrals) },
  ];
  return (
    <Card2>
      <SectionAccent title="Referral Velocity Funnel" sub={`${funnel.sent} provider sends · ${kpis.uniqueReferrals} unique referral IDs`} />
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
        {stages.map((s, i) => (
          <React.Fragment key={i}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div
                style={{
                  width: 180 - i * 16,
                  background: s.color,
                  height: 110 - i * 14,
                  borderRadius: "8px 8px 0 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <div style={{ color: "#fff", fontSize: "1.8rem", fontWeight: 800 }}>{s.value}</div>
                <div style={{ color: "#ccfffe", fontSize: "0.75rem", fontWeight: 600 }}>{s.label}</div>
                <div style={{ color: "#ccfffe", fontSize: "0.68rem" }}>{s.sub}</div>
              </div>
            </div>
            {i < stages.length - 1 && (
              <div style={{ color: "#00a7a6", fontSize: "1.2rem", margin: "0 6px", paddingBottom: 18 }}>→</div>
            )}
          </React.Fragment>
        ))}
      </div>
    </Card2>
  );
}

function ActivityByDay({ activity }) {
  const maxSend = Math.max(1, ...activity.map((d) => d.sends || 0));
  return (
    <Card2>
      <SectionAccent title="Referral Activity by Day of Week" sub="Best ingest window: Monday morning" />
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 140, paddingBottom: 4 }}>
        {activity.map((d) => {
          const sendPct = ((d.sends || 0) / maxSend) * 100;
          const finPct = ((d.finalized || 0) / maxSend) * 100;
          return (
            <div key={d.day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <div style={{ width: "100%", display: "flex", gap: 3, alignItems: "flex-end", height: 110 }}>
                <div style={{ flex: 1, background: "#00a7a6", borderRadius: "3px 3px 0 0", height: `${Math.max(sendPct, 1)}%` }} />
                <div style={{ flex: 1, background: "#16a34a", borderRadius: "3px 3px 0 0", height: `${Math.max(finPct, 1)}%` }} />
              </div>
              <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "#6b6969" }}>{d.day}</div>
              <div style={{ fontSize: "0.62rem", color: "#9b9898" }}>
                {d.sends} · {d.finalized}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 14, fontSize: "0.72rem", color: "#6b6969" }}>
        <span style={{ display: "inline-block", width: 12, height: 12, background: "#00a7a6", borderRadius: 2, marginRight: 6 }} />Sends
        <span style={{ display: "inline-block", width: 12, height: 12, background: "#16a34a", borderRadius: 2, margin: "0 6px 0 12px" }} />Finalizations
      </div>
    </Card2>
  );
}

function CanAcceptBacklog({ backlog }) {
  return (
    <Card2>
      <SectionAccent title="Can Accept Backlog" sub="Providers said YES · Coordinators have not finalized · Entries older than 28 days excluded" />
      {(!backlog || backlog.length === 0) ? (
        <div style={{ color: "#6b6969", padding: 12 }}>No open Can Accept entries within window.</div>
      ) : (
        <table className="orgreport-table">
          <thead>
            <tr><th>Facility</th><th>Provider</th><th style={{ textAlign: "right" }}>Sent</th><th style={{ textAlign: "right" }}>Wait</th></tr>
          </thead>
          <tbody>
            {backlog.map((row, i) => (
              <tr key={i} style={row.waitDays >= 14 ? { background: "#fff8f8" } : null}>
                <td style={{ fontWeight: 600, color: row.waitDays >= 14 ? "#dc2626" : "#d97706" }}>{row.facilityName}</td>
                <td style={{ color: "#6b6969", fontSize: "0.82rem" }}>{row.providerName}</td>
                <td style={{ textAlign: "right", color: "#6b6969", fontSize: "0.78rem" }}>{row.sentDate}</td>
                <td style={{ textAlign: "right" }}>
                  <span
                    style={{
                      background: row.waitDays >= 14 ? "#fee2e2" : "#fef3c7",
                      color: row.waitDays >= 14 ? "#991b1b" : "#92400e",
                      borderRadius: 4,
                      padding: "2px 7px",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                    }}
                  >
                    {row.waitDays}d
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card2>
  );
}

function FacilityNetwork({ facilities }) {
  const isActive = (f) => (f.uniqueRefs || 0) > 0 || (f.sends || 0) > 0;
  const activeFacilities = facilities.filter(isActive);
  const darkFacilities = facilities.filter((f) => !isActive(f));

  return (
    <Card2>
      <SectionAccent
        title="Facility Network — Active + Dark/Onboarding"
        sub="Active facilities (any referral activity in window) · Dark/Onboarding = no activity in selected period"
      />
      <table className="orgreport-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Facility</th>
            <th style={{ textAlign: "right" }}>Refs</th>
            <th style={{ textAlign: "right" }}>Sends</th>
            <th style={{ textAlign: "right" }}>Placed</th>
            <th style={{ width: 140 }}>Placement Rate</th>
            <th>Can Accept</th>
          </tr>
        </thead>
        <tbody>
          {activeFacilities.map((f, i) => {
            const rate = f.placementRatePct || 0;
            const color = rate >= 90 ? "#16a34a" : rate >= 60 ? "#00a7a6" : rate > 0 ? "#d97706" : "#dc2626";
            return (
              <tr key={f.facilityId || i}>
                <td>{rankMedal(i)}</td>
                <td style={{ fontWeight: 600 }}>{f.facilityName}</td>
                <td style={{ textAlign: "right" }}>{f.uniqueRefs}</td>
                <td style={{ textAlign: "right", color: "#6b6969" }}>{f.sends}</td>
                <td style={{ textAlign: "right", fontWeight: 600, color }}>{f.placed}</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ height: 6, width: 80, background: "#e1e0e0", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ height: 6, width: `${rate}%`, background: color, borderRadius: 999 }} />
                    </div>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color }}>{rate}%</span>
                  </div>
                </td>
                <td>
                  {f.openCanAccept > 0 ? (
                    <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "1px 6px", fontSize: "0.72rem", fontWeight: 700 }}>
                      {f.openCanAccept} CA · {f.maxCaWaitDays}d
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
          {darkFacilities.length > 0 && (
            <tr>
              <td colSpan={7} style={{ background: "#f4f3f1", borderTop: "2px solid #e1e0e0", borderBottom: "2px solid #e1e0e0", padding: "7px 12px" }}>
                <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#7b7979" }}>
                  ▼ Dark / Onboarding Facilities — No Activity in Selected Period
                </span>
              </td>
            </tr>
          )}
          {darkFacilities.map((f, i) => (
            <tr key={`dark-${f.facilityId || i}`} style={{ opacity: 0.7 }}>
              <td style={{ color: "#b0aeae" }}>—</td>
              <td style={{ color: "#6b6969" }}>{f.facilityName}</td>
              <td colSpan={2} style={{ textAlign: "right" }}>
                <span style={{ fontSize: "0.75rem", background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "2px 6px", fontWeight: 600 }}>
                  no activity
                </span>
              </td>
              <td style={{ color: "#b0aeae" }}>—</td>
              <td style={{ color: "#b0aeae" }}>—</td>
              <td>—</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card2>
  );
}

function SpeedToFinalize({ speed, kpis }) {
  const buckets = [
    { label: "< 24 hours", value: speed.lt24h, color: "#16a34a" },
    { label: "24 – 48 hours", value: speed.h24_48, color: "#00a7a6" },
    { label: "48 hrs – 3 days", value: speed.d2_3, color: "#00a7a6" },
    { label: "3 – 7 days", value: speed.d3_7, color: "#00a7a6" },
    { label: "7 – 14 days", value: speed.d7_14, color: "#d97706" },
    { label: "14+ days", value: speed.gt14d, color: "#dc2626" },
  ];
  return (
    <Card2>
      <SectionAccent
        title="Speed to Finalize"
        sub={`${speed.medianMinutes ?? "—"} min median · ${speed.lt24h}% within 24 hrs`}
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <BigStat value={speed.medianMinutes ?? "—"} unit="min median" sub="to finalize" />
            <BigStat value={speed.avgHours ?? "—"} unit="hrs average" sub="long-tail cases" color="#252424" />
          </div>
          <ProgressLine label="Within 24 hours" value={speed.lt24h} color="#16a34a" />
          <ProgressLine label="Within 48 hours" value={kpis.pctWithin48h} color="#00a7a6" />
          <ProgressLine label="Within 7 days" value={kpis.pctWithin7d} color="#00a7a6" />
        </div>
        <div>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b6969", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>
            Time-to-Finalize Distribution
          </div>
          {buckets.map((b) => (
            <div key={b.label} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: "0.82rem" }}>{b.label}</span>
                <span style={{ fontSize: "0.82rem", fontWeight: 700, color: b.color }}>{b.value}%</span>
              </div>
              <div style={{ height: 10, background: "#e1e0e0", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: 10, width: `${Math.max(b.value, 0)}%`, background: b.color, borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card2>
  );
}

function BigStat({ value, unit, sub, color = "#00a7a6" }) {
  return (
    <div style={{ background: "#f8f8f8", borderRadius: 10, padding: 16, textAlign: "center" }}>
      <div style={{ fontSize: "2.2rem", fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: "0.78rem", color: "#6b6969", fontWeight: 600 }}>{unit}</div>
      <div style={{ fontSize: "0.7rem", color: "#6b6969", marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function ProgressLine({ label, value, color }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: "0.82rem", color: "#252424" }}>{label}</span>
        <span style={{ fontWeight: 700, color }}>{value}%</span>
      </div>
      <div style={{ height: 6, background: "#e1e0e0", borderRadius: 999, overflow: "hidden", marginTop: 4 }}>
        <div style={{ height: 6, width: `${Math.max(value, 0)}%`, background: color, borderRadius: 999 }} />
      </div>
    </div>
  );
}

/* MaxAlly — fully static / mock as required */
function MaxAllySection() {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "4px 0", marginTop: 12 }}>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(to right,#00a7a640,#e1e0e0)" }} />
        <div style={{ background: "#252424", color: "#00a7a6", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "6px 16px", borderRadius: 20, border: "1px solid #00a7a640", whiteSpace: "nowrap" }}>
          MaxAlly Consumer Engagement · Beta Pilot
        </div>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(to left,#00a7a640,#e1e0e0)" }} />
      </div>
      <div style={{ background: "#fffbeb", border: "2px solid #fbbf24", borderRadius: 10, padding: "12px 20px", marginTop: 12 }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#92400e" }}>
          ⚠ MOCK DATA — Future MaxAlly Module
        </div>
        <div style={{ fontSize: "0.72rem", color: "#b45309", marginTop: 2 }}>
          Illustrative values only. Real metrics will require new CSV fields (Facility_ID, Payer_Type, DRG_Category,
          Readmission_Date/Window, AI_Flags_Raised/Acted_On, Survey_Flags_Raised, Discharge_Destination, Referring_Hospital).
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginTop: 12 }}>
        <MockKpi label="Caregiver Activation" value="67%" sub="avg across 4 facilities" />
        <MockKpi label="30-Day Readmit · Engaged" value="4.8%" sub="MaxAlly-enrolled patients" color="#16a34a" />
        <MockKpi label="30-Day Readmit · Not Enrolled" value="15.6%" sub="3.2× higher risk" color="#dc2626" />
        <MockKpi label="AI Flags → Acted On" value="9 / 11" sub="82% response rate" color="#d97706" />
        <MockKpi label="Survey Completion" value="84%" sub="11 survey flags raised" color="#7c3aed" />
      </div>
      <div style={{ marginTop: 16, background: "linear-gradient(135deg,#f0fffe 0%,#eafffe 100%)", border: "2px solid #00a7a640", borderRadius: 12, padding: 20, textAlign: "center" }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#00706f", marginBottom: 8 }}>
          30-Day Readmit Rate · CMS VBP Penalty Window
        </div>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 24 }}>
          <div>
            <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "#16a34a" }}>4.8%</div>
            <div style={{ fontSize: "0.72rem", color: "#6b6969" }}>MaxAlly enrolled</div>
          </div>
          <div style={{ fontSize: "1.2rem", color: "#b0aeae" }}>vs</div>
          <div>
            <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "#dc2626" }}>15.6%</div>
            <div style={{ fontSize: "0.72rem", color: "#6b6969" }}>not enrolled</div>
          </div>
        </div>
        <div style={{ fontSize: "0.75rem", color: "#dc2626", fontWeight: 700, marginTop: 10 }}>
          3.2× higher risk if not enrolled → $17K–$34K VBP penalty avoidance / facility
        </div>
      </div>
    </div>
  );
}

function MockKpi({ label, value, sub, color = "#00a7a6" }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e1e0e0", borderTop: `3px solid ${color}`, borderRadius: 14, padding: 16, textAlign: "center", position: "relative" }}>
      <div style={{ position: "absolute", top: 6, right: 8, fontSize: "0.6rem", background: "#fef3c7", color: "#92400e", borderRadius: 3, padding: "1px 5px", fontWeight: 700 }}>MOCK</div>
      <div className="section-label">{label}</div>
      <div style={{ fontSize: "2.1rem", fontWeight: 800, color, lineHeight: 1, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: "0.78rem", color: "#6b6969", marginTop: 4 }}>{sub}</div>
    </div>
  );
}

/* ---------- small helpers ---------- */

function Card2({ children }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e1e0e0",
        borderRadius: 14,
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}

function SectionAccent({ title, sub }) {
  return (
    <div style={{ borderLeft: "3px solid #00a7a6", paddingLeft: 12, marginBottom: 16 }}>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, letterSpacing: "-0.02em", color: "#1a1919" }}>{title}</div>
      {sub && <div style={{ fontSize: "0.78rem", color: "#6b6969" }}>{sub}</div>}
    </div>
  );
}

function rankMedal(i) {
  if (i === 0) return <span style={{ color: "#b45309", fontWeight: 700 }}>🥇</span>;
  if (i === 1) return <span style={{ color: "#64748b", fontWeight: 700 }}>🥈</span>;
  if (i === 2) return <span style={{ color: "#92400e", fontWeight: 700 }}>🥉</span>;
  return <span style={{ color: "#6b6969", fontWeight: 600 }}>{i + 1}</span>;
}

function pctOf(num, denom) {
  if (!denom) return "—";
  return `${Math.round((num / denom) * 1000) / 10}%`;
}

function formatDate(d) {
  if (!d) return "";
  return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}, ${d.getFullYear()}`;
}
